<template>
    <div class="automation-run-wizard">
        <div class="container-xl">
            <div class="card page-card automation-wizard-card">
                <div class="card-header d-flex align-items-center">
                    <div class="title d-flex align-items-center">
                        <span class="automation-wizard-card__icon"><i class="far fa-fw fa-magic"></i></span> <slot name="title"></slot>
                    </div>
                </div>

                <!-- where the user is: inputs -> preview of the changes -> result -->
                <ol class="automation-steps">
                    <li v-for="(item, index) in steps" :key="item.name" class="automation-steps__step"
                        :class="{'is-active': item.name === step, 'is-done': index < stepIndex}">
                        <span class="automation-steps__number">
                            <i class="fa fa-check" v-if="index < stepIndex"></i>
                            <template v-else>{{ index + 1 }}</template>
                        </span>
                        <span class="automation-steps__label">{{ item.label }}</span>
                    </li>
                </ol>

                <div class="card-block">
                    <!-- step 1: inputs -->
                    <template v-if="step === 'inputs'">
                        <p class="automation-wizard-card__description mb-0"><slot name="description"></slot></p>

                        <form class="automation-wizard-inputs" @submit.prevent="loadPreview()">
                            <div class="form-group automation-wizard-inputs__input" v-for="input in inputs" :key="input.name">
                                <label class="input-label" :for="'automation-input-' + input.name">{{ input.label }}</label>
                                <dropdown-months v-if="input.type === 'month'"
                                    :trans="transMonths"
                                    :year-count="5"
                                    :year="monthPart(values[input.name], 0)"
                                    :month="monthPart(values[input.name], 1)"
                                    :choose-action="(year, month) => chooseMonth(input.name, year, month)"
                                    :loading="loading"
                                    :show-buttons="true"
                                ></dropdown-months>
                                <input v-else
                                    :id="'automation-input-' + input.name"
                                    type="text"
                                    class="form-control"
                                    :class="{'is-invalid': errors[input.name]}"
                                    v-model="values[input.name]">
                                <div class="invalid-feedback d-block" v-if="errors[input.name]">{{ errors[input.name][0] }}</div>
                            </div>
                        </form>
                    </template>

                    <!-- step 2: preview, what the confirmation will write -->
                    <template v-if="step === 'preview' && run">
                        <div class="alert alert-warning" v-if="previewChanged">
                            <i class="far fa-exclamation-triangle"></i> {{ trans('preview_changed') }}
                        </div>

                        <div class="automation-preview__intro">
                            <i class="far fa-info-circle"></i>
                            <div class="automation-preview__intro-text">
                                {{ changes.length ? trans('preview_intro') : trans('no_changes') }}
                                <div class="automation-preview__inputs">{{ trans('inputs') }}: <b>{{ run.inputs }}</b></div>
                            </div>
                            <div class="automation-preview__stats" v-if="changes.length">
                                <div class="automation-preview__stat">
                                    <div class="automation-preview__stat-label">{{ run.subjects_label }}</div>
                                    <div class="automation-preview__stat-value">{{ changes.length }}</div>
                                </div>
                                <div class="automation-preview__stat" v-if="run.changes_total">
                                    <div class="automation-preview__stat-label">{{ trans('total') }}</div>
                                    <div class="automation-preview__stat-value is-total">{{ run.changes_total }}</div>
                                </div>
                            </div>
                        </div>

                        <div class="automation-preview__section" v-for="section in previewSections" :key="section.name">
                            <h6 class="automation-preview__heading" :class="'is-' + section.name">
                                <i :class="section.icon"></i> {{ section.label }}
                                <span class="automation-preview__count" v-if="!section.collapsible">{{ section.items.length }}</span>
                                <button type="button" class="automation-preview__toggle" v-if="section.collapsible" @click="showUnchanged = !showUnchanged">
                                    {{ showUnchanged ? trans('hide') : trans('show') }}
                                    <i class="far fa-chevron-down ml-1" :class="{'fa-rotate-180': showUnchanged}"></i>
                                </button>
                            </h6>

                            <ul class="automation-changes" v-if="!section.collapsible || showUnchanged">
                                <li class="automation-change" v-for="item in section.items" :key="item.key">
                                    <span class="automation-change__avatar">{{ initials(item) }}</span>

                                    <div class="automation-change__subject">
                                        <div class="automation-change__title">{{ title(item) }}</div>
                                        <div class="automation-change__badges" v-if="item.columns">
                                            <span class="automation-change__badge" v-for="column in badgeColumns" :key="column.key"
                                                :class="{'is-highlighted': column.highlighted_by && item.columns[column.highlighted_by]}">
                                                <template v-if="column.display === 'labeled-badge'">{{ column.label }}: </template>{{ item.columns[column.key] }}
                                            </span>
                                        </div>
                                    </div>

                                    <div class="automation-change__progress" v-for="column in (item.columns ? progressColumns : [])" :key="column.key">
                                        <div class="automation-change__progress-label">
                                            <span>{{ column.label }}</span>
                                            <b>{{ item.columns[column.key] }}</b>
                                        </div>
                                        <div class="automation-change__bar" :class="{'is-empty': progress(item.columns[column.key]) === null}">
                                            <span :style="{width: (progress(item.columns[column.key]) || 0) + '%'}"></span>
                                        </div>
                                    </div>

                                    <div class="automation-change__values" v-if="section.name === 'changes'">
                                        <span class="automation-change__old">{{ item.old_value }}</span>
                                        <i class="far fa-long-arrow-right"></i>
                                        <span class="automation-change__new">{{ item.new_value }}</span>
                                    </div>
                                    <div class="automation-change__values" v-else-if="section.name === 'errors'">
                                        <span class="automation-change__error">{{ item.error }}</span>
                                    </div>
                                    <div class="automation-change__values" v-else>
                                        <span class="automation-change__same">{{ item.old_value }}</span>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </template>

                    <!-- step 3: result, what was written and what was not (with the reason) -->
                    <template v-if="step === 'result' && run">
                        <div class="automation-result__done" v-if="!failedItems.length">
                            <span class="automation-result__done-icon"><i class="fa fa-check"></i></span>
                            <div>
                                <div class="automation-result__done-title">{{ trans('all_written_title') }}</div>
                                <div class="automation-result__done-text">{{ trans('all_written_text') }}</div>
                            </div>
                        </div>

                        <div class="automation-result__stats">
                            <div class="automation-result__stat is-success">
                                <span class="automation-result__stat-icon"><i class="fa fa-check"></i></span>
                                <div class="automation-result__stat-main">
                                    <div class="automation-result__stat-label">{{ trans('success_written') }}</div>
                                    <div class="automation-result__stat-value">{{ writtenItems.length }}</div>
                                </div>
                                <div class="automation-result__stat-side" v-if="run.success_total">
                                    <div class="automation-result__stat-label">{{ trans('sum') }}</div>
                                    <div class="automation-result__stat-value is-total">{{ run.success_total }}</div>
                                </div>
                            </div>
                            <div class="automation-result__stat is-failed">
                                <span class="automation-result__stat-icon"><i class="fa fa-times"></i></span>
                                <div class="automation-result__stat-main">
                                    <div class="automation-result__stat-label">{{ trans('failed_written') }}</div>
                                    <div class="automation-result__stat-value">{{ failedItems.length }}</div>
                                </div>
                                <div class="automation-result__stat-side" v-for="input in run.inputs_list" :key="input.label">
                                    <div class="automation-result__stat-label">{{ input.label }}</div>
                                    <div class="automation-result__stat-value">{{ input.value }}</div>
                                </div>
                            </div>
                        </div>

                        <div class="automation-preview__section" v-for="section in resultSections" :key="section.name">
                            <h6 class="automation-preview__heading" :class="'is-' + section.name">
                                <i :class="section.icon"></i> {{ section.label }}
                                <span class="automation-preview__count" v-if="!section.collapsible">{{ section.items.length }}</span>
                                <button type="button" class="automation-preview__toggle" v-if="section.collapsible" @click="showUnchanged = !showUnchanged">
                                    {{ showUnchanged ? trans('hide') : trans('show') }}
                                    <i class="far fa-chevron-down ml-1" :class="{'fa-rotate-180': showUnchanged}"></i>
                                </button>
                            </h6>

                            <ul class="automation-changes" v-if="!section.collapsible || showUnchanged">
                                <li class="automation-change" :class="'is-' + section.name" v-for="item in section.items" :key="item.key">
                                    <span class="automation-change__avatar">{{ initials(item) }}</span>

                                    <div class="automation-change__subject">
                                        <div class="automation-change__title">{{ title(item) }}</div>
                                        <div class="automation-change__reason" v-if="section.name === 'failed'">
                                            <i class="far fa-exclamation-triangle"></i> {{ item.error }}
                                        </div>
                                        <div class="automation-change__badges" v-else-if="item.columns">
                                            <span class="automation-change__badge" v-for="column in badgeColumns" :key="column.key">
                                                <template v-if="column.display === 'labeled-badge'">{{ column.label }}: </template>{{ item.columns[column.key] }}
                                            </span>
                                        </div>
                                    </div>

                                    <div class="automation-change__values" v-if="section.name === 'failed'">
                                        <span class="automation-change__not-written" v-if="item.new_value !== item.old_value">{{ item.new_value }} {{ trans('not_written') }}</span>
                                    </div>
                                    <div class="automation-change__values" v-else-if="section.name === 'written'">
                                        <span class="automation-change__new">{{ item.new_value }}</span>
                                    </div>
                                    <div class="automation-change__values" v-else>
                                        <span class="automation-change__same">{{ item.old_value }}</span>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </template>
                </div>

                <!-- the row with the buttons like in the settings of the contract type, pinned to the bottom of the window when out of view -->
                <div class="automation-run-footer-holder" ref="footerHolder">
                    <div class="automation-run-footer" :class="{'is-pinned': footerPinned}" :style="footerStyle">
                        <span class="automation-run-footer__total">
                            <template v-if="step === 'preview' && run && run.changes_total && changes.length">{{ trans('will_write') }} <b>{{ run.changes_total }}</b></template>
                            <template v-if="step === 'result' && run && run.success_total">{{ trans('written') }} <b>{{ run.success_total }}</b></template>
                        </span>

                        <div v-if="step === 'inputs'">
                            <a :href="indexUrl" class="btn btn-secondary"><i class="fa fa-arrow-left"></i> {{ trans('back') }}</a>
                            <b-button type="button" variant="primary" @click="loadPreview()" :disabled="loading">
                                <i class="fas fa-eye"></i> {{ trans('show_preview') }}
                            </b-button>
                        </div>
                        <div v-else-if="step === 'result'">
                            <b-button type="button" variant="secondary" @click="retry()" :disabled="loading" v-if="run && run.retry_url">
                                <i class="fas fa-redo"></i> {{ trans('retry_failed') }}
                            </b-button>
                            <a :href="indexUrl" class="btn btn-primary"><i class="fa fa-times"></i> {{ trans('close') }}</a>
                        </div>
                        <div v-else>
                            <b-button type="button" variant="secondary" @click="restart()" :disabled="loading">
                                <i class="fa fa-arrow-left"></i> {{ trans('back') }}
                            </b-button>
                            <b-button type="button" variant="primary" @click="$modal.show('automation-confirm-modal')" :disabled="loading || !changes.length">
                                <i class="fa fa-check"></i> {{ trans('confirm') }}
                            </b-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="loading-box" v-show="loading">
            <div class="loading-inner">
                <h2 class="mb-5 title">{{ trans('processing') }}&hellip;</h2>
                <i class="fa fa-spin fa-circle-notch text-secondary"></i>
            </div>
        </div>

        <modal
            name="automation-confirm-modal"
            class="automation-confirm-modal modal--hide-on-loading"
            v-cloak
            height="auto"
            :width="460"
            :scrollable="true"
            :adaptive="true"
            :pivot-y="0.25"
        >
            <button @click="$modal.hide('automation-confirm-modal')" type="button" class="automation-confirm-modal__close"><i class="fal fa-fw fa-times"></i></button>

            <div class="automation-confirm-modal__body">
                <span class="automation-confirm-modal__icon"><i class="far fa-pen"></i></span>
                <h5 class="automation-confirm-modal__title">{{ trans('modal_confirm_title') }}</h5>
                <p class="automation-confirm-modal__text">{{ trans('modal_confirm_text') }}</p>
            </div>

            <div class="automation-confirm-modal__footer">
                <b-button type="button" variant="light" @click="$modal.hide('automation-confirm-modal')"><i class="fa fa-arrow-left mr-2 text-dark"></i> {{ trans('back') }}</b-button>
                <b-button type="button" variant="primary" @click="confirm()" :class="{'disabled': loading}"><i class="fa fa-check mr-2"></i> {{ trans('confirm') }}</b-button>
            </div>
        </modal>
    </div>
</template>

<script>
    import TranslationsUtils from "../app-components/TranslationsUtils";
    import DropdownMonths from "./basic/DropdownMonths";
    import AutomationRunItems from "./AutomationRunItems";

    export default {
        mixins: [TranslationsUtils, AutomationRunItems],
        components: { DropdownMonths },
        props: {
            inputs: { type: Array },
            transMonths: { type: Object },
            previewUrl: { type: String },
            indexUrl: { type: String }
        },

        data() {
            return {
                step: 'inputs',
                values: this.inputs.reduce((acc, input) => {
                    acc[input.name] = input.default;
                    return acc;
                }, {}),
                errors: {},
                run: null,
                previewChanged: false,
                loading: false
            };
        },

        computed: {
            steps() {
                return [
                    { name: 'inputs', label: this.trans('inputs') },
                    { name: 'preview', label: this.trans('preview') },
                    { name: 'result', label: this.trans('result') }
                ];
            },

            stepIndex() {
                return this.steps.findIndex(item => item.name === this.step);
            },

            changes() {
                return this.sortedItems.filter(item => item.status_code === 'pending');
            },

            // what the confirmation writes, what it cannot write (with the reason) and what stays as it is
            previewSections() {
                return [
                    { name: 'changes', label: this.trans('section_changes'), icon: 'far fa-pen', items: this.changes },
                    { name: 'errors', label: this.trans('section_errors'), icon: 'far fa-exclamation-triangle', items: this.failedItems },
                    { name: 'unchanged', label: this.trans('unchanged_count'), icon: 'far fa-equals', items: this.unchangedItems, collapsible: true }
                ].filter(section => section.items.length);
            },

            // what was not written (with the reason, it can be tried again), what was written and what stayed as it was
            resultSections() {
                return [
                    { name: 'failed', label: this.trans('failed_written'), icon: 'far fa-exclamation-circle', items: this.failedItems },
                    { name: 'written', label: this.trans('written'), icon: 'far fa-check', items: this.writtenItems },
                    { name: 'unchanged', label: this.trans('unchanged_count'), icon: 'far fa-equals', items: this.unchangedItems, collapsible: true }
                ].filter(section => section.items.length);
            }
        },

        methods: {
            loadPreview() {
                this.previewChanged = false;
                this.post(this.previewUrl, this.values, 'preview');
            },

            // the preview is not stored, the confirmation calculates the changes again from the same inputs
            // and writes them only when they are the same as the ones the user saw (the fingerprint of the preview)
            confirm() {
                this.$modal.hide('automation-confirm-modal');
                this.previewChanged = false;
                this.post(this.run.confirm_url, Object.assign({}, this.values, { fingerprint: this.run.fingerprint }), 'result');
            },

            // the failed changes are calculated again and written, the written ones stay as they are
            retry() {
                this.post(this.run.retry_url, {}, 'result');
            },

            // a month input keeps the "Y-m" value, the month picker works with the year and the month as numbers
            monthPart(value, index) {
                return value ? parseInt(value.split('-')[index]) : null;
            },

            chooseMonth(name, year, month) {
                this.values[name] = year + '-' + (month < 10 ? '0' : '') + month;
            },

            restart() {
                this.run = null;
                this.step = 'inputs';
                this.showUnchanged = false;
                this.previewChanged = false;
            },

            post(url, data, nextStep) {
                this.loading = true;
                this.errors = {};
                axios.post(url, data).then((response) => {
                    this.loading = false;
                    this.run = this.withPreviewColumns(response.data.data);
                    this.step = nextStep;
                }, (error) => {
                    this.loading = false;
                    if (error.response && error.response.status === 422) {
                        this.errors = error.response.data.errors || {};
                        return;
                    }
                    // the values changed since the preview, nothing was written: the user checks the new preview and confirms it again
                    if (error.response && error.response.status === 409 && error.response.data.data) {
                        this.run = error.response.data.data;
                        this.step = 'preview';
                        this.previewChanged = true;
                    }
                    this.notifyError(error);
                });
            },

            // the preview columns are not stored, so the result of the confirmation (or the retry) keeps them from the preview
            withPreviewColumns(run) {
                if (!this.run || run.columns.length) {
                    return run;
                }

                const columns = {};
                this.run.items.forEach(item => { columns[item.key] = item.columns; });

                return Object.assign({}, run, {
                    columns: this.run.columns,
                    items: run.items.map(item => Object.assign({}, item, { columns: columns[item.key] || null }))
                });
            },

            notifyError(error) {
                this.$notify({
                    type: 'error',
                    title: this.trans('error'),
                    text: error.response && (error.response.data.error || error.response.data.message) ? (error.response.data.error || error.response.data.message) : this.trans('unknown_error')
                });
            }
        }
    };
</script>



// WEBPACK FOOTER //
// resources/assets/admin/js/components/AutomationRunWizard.vue